import { useState } from "react";

import { useDistribution } from "../api/hooks/useDistribution";
import { useOverallStatistics } from "../api/hooks/useOverallStatistics";
import { usePnLByDay } from "../api/hooks/usePnLByDay";
import type { components } from "../api/schema";
import { Card, CardContent } from "../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { useGlobalFilters } from "../lib/globalFilters";
import { formatMoney } from "../lib/money";
import { cn } from "../lib/cn";

type OverallStatisticsResponse = components["schemas"]["OverallStatisticsResponse"];
type PnLView = OverallStatisticsResponse["pnl"];
type ScoresMetricsView = OverallStatisticsResponse["scores_metrics"];
type TotalTradesView = OverallStatisticsResponse["total_trades"];
type VolumeView = OverallStatisticsResponse["volume"];
type SymbolView = OverallStatisticsResponse["symbol"];
type TradeDurationView = OverallStatisticsResponse["trade_duration"];
type CommissionsView = OverallStatisticsResponse["commissions"];
type DailyPnLPoint = components["schemas"]["DailyPnLPointResponse"];
type PnLDistribution = components["schemas"]["PnLDistributionResponse"];

// The seven aggregate views back onto the overall-statistics query; the Daily
// P&L table backs onto pnl-by-day, and the Distribution view backs onto the
// per-trade pnl-distribution query.
const STATS_VIEW_IDS = [
  "pnl",
  "scores_metrics",
  "total_trades",
  "volume",
  "symbol",
  "trade_duration",
  "commissions",
] as const;
const VIEW_IDS = [...STATS_VIEW_IDS, "daily_pnl", "distribution"] as const;
type ViewId = (typeof VIEW_IDS)[number];

const VIEW_LABELS: Record<ViewId, string> = {
  pnl: "P&L",
  scores_metrics: "Scores & Metrics",
  total_trades: "Total Trades",
  volume: "Volume",
  symbol: "Symbol",
  trade_duration: "Trade Duration",
  commissions: "Commissions",
  daily_pnl: "Daily P&L",
  distribution: "Distribution",
};

function isStatsView(view: ViewId): boolean {
  return (STATS_VIEW_IDS as ReadonlyArray<string>).includes(view);
}

type Nullable = string | null | undefined;

function safeFormatMoney(amount: Nullable, currency: Nullable): string {
  if (amount === null || amount === undefined) return "—";
  if (currency === null || currency === undefined) return "—";
  try {
    return formatMoney(amount, { currency });
  } catch {
    return `${amount} ${currency}`;
  }
}

function formatPercent(decimal: Nullable, options: { decimals?: number } = {}): string {
  if (decimal === null || decimal === undefined) return "—";
  const n = Number(decimal);
  if (Number.isNaN(n)) return decimal;
  const decimals = options.decimals ?? 2;
  return `${(n * 100).toFixed(decimals)}%`;
}

function formatDecimal(decimal: Nullable, options: { decimals?: number } = {}): string {
  if (decimal === null || decimal === undefined) return "—";
  const n = Number(decimal);
  if (Number.isNaN(n)) return decimal;
  const decimals = options.decimals ?? 2;
  return n.toLocaleString("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

function formatInteger(n: number): string {
  return n.toLocaleString("en-US");
}

function formatHoldTime(iso: Nullable): string {
  if (iso === null || iso === undefined) return "—";
  // ISO-8601 duration like "PT2H30M" or "P1DT4H". Compute total seconds via regex.
  const match = /^P(?:(\d+)D)?(?:T(?:(\d+)H)?(?:(\d+)M)?(?:(\d+(?:\.\d+)?)S)?)?$/.exec(iso);
  if (!match) return iso;
  const [, d, h, m, s] = match;
  const days = Number(d ?? 0);
  const hours = Number(h ?? 0);
  const minutes = Number(m ?? 0);
  const seconds = Number(s ?? 0);
  const totalSec = days * 86400 + hours * 3600 + minutes * 60 + seconds;
  if (totalSec >= 86400) {
    return `${(totalSec / 86400).toFixed(1)}d`;
  }
  if (totalSec >= 3600) {
    return `${(totalSec / 3600).toFixed(1)}h`;
  }
  if (totalSec >= 60) {
    return `${(totalSec / 60).toFixed(0)}m`;
  }
  return `${totalSec.toFixed(0)}s`;
}

interface StatCardProps {
  label: string;
  value: string;
  subLabel?: string;
  testId?: string;
}

function StatCard({ label, value, subLabel, testId }: StatCardProps) {
  return (
    <Card data-testid={testId}>
      <CardContent className="flex flex-col gap-1 pt-4">
        <div className="text-xs uppercase tracking-wide text-on-surface-variant">{label}</div>
        <div className="text-lg font-semibold text-on-surface">{value}</div>
        {subLabel !== undefined && (
          <div className="text-xs text-on-surface-variant">{subLabel}</div>
        )}
      </CardContent>
    </Card>
  );
}

function PnLViewPanel({ view, currency }: { view: PnLView; currency: string }) {
  return (
    <div
      className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3"
      data-testid="overall-stats-pnl-panel"
    >
      <StatCard
        label="Total Net P&L"
        value={safeFormatMoney(view.total_net_pnl_amount, view.total_net_pnl_currency)}
      />
      <StatCard
        label="Expectancy"
        value={safeFormatMoney(view.expectancy_amount, view.expectancy_currency)}
        subLabel="Per-trade expected value"
      />
      <StatCard label="Win Rate" value={formatPercent(view.win_rate)} />
      <StatCard label="Loss Rate" value={formatPercent(view.loss_rate)} />
      <StatCard label="Profit Factor" value={formatDecimal(view.profit_factor)} />
      <StatCard
        label="Avg R-Multiple"
        value={formatDecimal(view.average_r_multiple)}
      />
      <StatCard
        label="Average Win"
        value={safeFormatMoney(view.average_win_amount, view.average_win_currency)}
      />
      <StatCard
        label="Average Loss"
        value={safeFormatMoney(view.average_loss_amount, view.average_loss_currency)}
      />
      <StatCard
        label="Largest Win"
        value={safeFormatMoney(view.largest_win_amount, view.largest_win_currency)}
      />
      <StatCard
        label="Largest Loss"
        value={safeFormatMoney(view.largest_loss_amount, view.largest_loss_currency)}
      />
      <StatCard
        label="Sum of Wins"
        value={safeFormatMoney(view.sum_of_wins_amount, view.sum_of_wins_currency)}
      />
      <StatCard
        label="Sum of Losses"
        value={safeFormatMoney(view.sum_of_losses_amount, view.sum_of_losses_currency)}
      />
      <input type="hidden" data-testid="pnl-view-currency" value={currency} readOnly />
    </div>
  );
}

function ScoresMetricsViewPanel({ view }: { view: ScoresMetricsView }) {
  return (
    <div
      className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3"
      data-testid="overall-stats-scores-panel"
    >
      <StatCard
        label="Total Account Value"
        value={safeFormatMoney(
          view.total_account_value_amount,
          view.total_account_value_currency,
        )}
      />
      <StatCard
        label="Total Deposits"
        value={safeFormatMoney(view.total_deposits_amount, view.total_deposits_currency)}
      />
      <StatCard
        label="Total Withdrawals"
        value={safeFormatMoney(
          view.total_withdrawals_amount,
          view.total_withdrawals_currency,
        )}
      />
      <StatCard
        label="Total Dividends"
        value={safeFormatMoney(view.total_dividends_amount, view.total_dividends_currency)}
      />
      <StatCard
        label="P&L Std Dev"
        value={safeFormatMoney(view.pnl_std_dev_amount, view.pnl_std_dev_currency)}
        subLabel="Daily series, population stddev"
      />
      <StatCard
        label="Sharpe-Like Ratio"
        value={formatDecimal(view.sharpe_like_ratio, { decimals: 3 })}
        subLabel="Mean / std-dev (daily)"
      />
      <StatCard
        label="Max Drawdown"
        value={safeFormatMoney(view.max_drawdown_amount, view.max_drawdown_currency)}
      />
    </div>
  );
}

function TotalTradesViewPanel({ view }: { view: TotalTradesView }) {
  return (
    <div
      className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4"
      data-testid="overall-stats-total-trades-panel"
    >
      <StatCard label="Total Trades" value={formatInteger(view.trade_count)} />
      <StatCard label="Wins" value={formatInteger(view.win_count)} />
      <StatCard label="Losses" value={formatInteger(view.loss_count)} />
      <StatCard label="Scratch" value={formatInteger(view.scratch_count)} />
      <StatCard
        label="Distinct Trading Days"
        value={formatInteger(view.distinct_trading_days)}
      />
      <StatCard
        label="Avg Trades / Day"
        value={formatDecimal(view.average_trades_per_day)}
      />
      <StatCard
        label="Longest Win Streak"
        value={formatInteger(view.longest_win_streak)}
      />
      <StatCard
        label="Longest Loss Streak"
        value={formatInteger(view.longest_loss_streak)}
      />
    </div>
  );
}

function VolumeViewPanel({ view }: { view: VolumeView }) {
  return (
    <div
      className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3"
      data-testid="overall-stats-volume-panel"
    >
      <StatCard
        label="Total Volume"
        value={formatDecimal(view.total_volume, { decimals: 0 })}
        subLabel="Shares / contracts opened"
      />
      <StatCard
        label="Avg Volume / Trade"
        value={formatDecimal(view.average_volume_per_trade)}
      />
      <StatCard
        label="Median Volume / Trade"
        value={formatDecimal(view.median_volume_per_trade)}
      />
      <StatCard
        label="Largest Volume Trade"
        value={formatDecimal(view.largest_volume_trade, { decimals: 0 })}
      />
      <StatCard
        label="Avg Volume on Wins"
        value={formatDecimal(view.average_volume_on_wins)}
      />
      <StatCard
        label="Avg Volume on Losses"
        value={formatDecimal(view.average_volume_on_losses)}
      />
    </div>
  );
}

function SymbolViewPanel({ view }: { view: SymbolView }) {
  const mostTradedLabel = view.most_traded_symbol ?? "—";
  const mostTradedSub =
    view.most_traded_symbol_trade_count > 0
      ? `${view.most_traded_symbol_trade_count} trades`
      : undefined;
  const bestLabel = view.best_symbol_by_pnl_label ?? "—";
  const bestFormatted = safeFormatMoney(
    view.best_symbol_by_pnl_net_amount,
    view.best_symbol_by_pnl_net_currency,
  );
  const bestSub = bestFormatted === "—" ? undefined : bestFormatted;
  const worstLabel = view.worst_symbol_by_pnl_label ?? "—";
  const worstFormatted = safeFormatMoney(
    view.worst_symbol_by_pnl_net_amount,
    view.worst_symbol_by_pnl_net_currency,
  );
  const worstSub = worstFormatted === "—" ? undefined : worstFormatted;
  return (
    <div
      className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3"
      data-testid="overall-stats-symbol-panel"
    >
      <StatCard
        label="Distinct Symbols"
        value={formatInteger(view.distinct_symbol_count)}
      />
      <StatCard label="Most Traded" value={mostTradedLabel} subLabel={mostTradedSub} />
      <StatCard
        label="Avg Trades / Symbol"
        value={formatDecimal(view.average_trades_per_symbol)}
      />
      <StatCard label="Best by P&L" value={bestLabel} subLabel={bestSub} />
      <StatCard label="Worst by P&L" value={worstLabel} subLabel={worstSub} />
    </div>
  );
}

function TradeDurationViewPanel({ view }: { view: TradeDurationView }) {
  return (
    <div
      className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3"
      data-testid="overall-stats-duration-panel"
    >
      <StatCard label="Average Hold Time" value={formatHoldTime(view.average_hold_time)} />
      <StatCard label="Median Hold Time" value={formatHoldTime(view.median_hold_time)} />
      <StatCard label="Shortest Trade" value={formatHoldTime(view.shortest_hold_time)} />
      <StatCard label="Longest Trade" value={formatHoldTime(view.longest_hold_time)} />
      <StatCard
        label="Avg Hold (Wins)"
        value={formatHoldTime(view.average_hold_time_on_wins)}
      />
      <StatCard
        label="Avg Hold (Losses)"
        value={formatHoldTime(view.average_hold_time_on_losses)}
      />
    </div>
  );
}

function CommissionsViewPanel({ view }: { view: CommissionsView }) {
  return (
    <div
      className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3"
      data-testid="overall-stats-commissions-panel"
    >
      <StatCard
        label="Total Fees"
        value={safeFormatMoney(view.total_fees_amount, view.total_fees_currency)}
      />
      <StatCard
        label="Avg Fees / Trade"
        value={safeFormatMoney(
          view.average_fees_per_trade_amount,
          view.average_fees_per_trade_currency,
        )}
      />
      <StatCard
        label="Avg Fees / Win"
        value={safeFormatMoney(
          view.average_fees_per_win_amount,
          view.average_fees_per_win_currency,
        )}
      />
      <StatCard
        label="Avg Fees / Loss"
        value={safeFormatMoney(
          view.average_fees_per_loss_amount,
          view.average_fees_per_loss_currency,
        )}
      />
      <StatCard
        label="Largest Single Fee"
        value={safeFormatMoney(
          view.largest_single_trade_fees_amount,
          view.largest_single_trade_fees_currency,
        )}
      />
      <StatCard
        label="Fees % of Gross Profit"
        value={formatPercent(view.fees_as_pct_of_gross_profit, { decimals: 1 })}
        subLabel="Total fees / sum of wins"
      />
    </div>
  );
}

function DailyPnLViewPanel({
  points,
  currency,
}: {
  points: ReadonlyArray<DailyPnLPoint>;
  currency: string;
}) {
  return (
    <div className="space-y-3" data-testid="overall-stats-daily-pnl-panel">
      {points.length === 0 ? (
        <p className="text-sm text-on-surface-variant">No realized P&amp;L recorded yet.</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date (UTC)</TableHead>
              <TableHead className="text-right">Amount</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {points.map((point) => (
              <TableRow key={point.trade_date}>
                <TableCell>{point.trade_date}</TableCell>
                <TableCell className="text-right">
                  {safeFormatMoney(point.pnl_amount, currency)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

// Per-trade distribution. The server returns two five-band signed histograms
// (P&L + R-multiple) over CLOSED trades; the tab toggles between them with no
// refetch. Band labels live here; the P&L band ranges are rendered from the
// server's magnitude-derived threshold, the R band edges are the fixed ±1R.
type DistMode = "pnl" | "r_multiple";

const DIST_BAND_LABELS: Record<string, string> = {
  "large-loss": "Large loss",
  "small-loss": "Small loss",
  breakeven: "Breakeven",
  "small-win": "Small win",
  "large-win": "Large win",
};

function pnlBandRange(id: string, edge: string): string {
  switch (id) {
    case "large-loss":
      return `≤ -${edge}`;
    case "small-loss":
      return `-${edge} … $0`;
    case "breakeven":
      return "$0";
    case "small-win":
      return `$0 … ${edge}`;
    case "large-win":
      return `≥ ${edge}`;
    default:
      return "";
  }
}

function rBandRange(id: string): string {
  switch (id) {
    case "large-loss":
      return "≤ -1R";
    case "small-loss":
      return "-1R … 0";
    case "breakeven":
      return "0R";
    case "small-win":
      return "0 … 1R";
    case "large-win":
      return "≥ 1R";
    default:
      return "";
  }
}

function DistributionViewPanel({ data }: { data: PnLDistribution }) {
  const [mode, setMode] = useState<DistMode>("pnl");

  if (data.trade_count === 0) {
    return (
      <div data-testid="overall-stats-distribution-panel">
        <p className="text-sm text-on-surface-variant">No closed trades recorded yet.</p>
      </div>
    );
  }

  const edge = safeFormatMoney(data.pnl_threshold_amount, data.currency);
  const buckets = mode === "pnl" ? data.pnl_buckets : data.r_buckets;
  const maxCount = buckets.reduce((m, b) => Math.max(m, b.count), 0);
  const activeCount = mode === "pnl" ? data.trade_count : data.r_trade_count;

  return (
    <div className="space-y-4" data-testid="overall-stats-distribution-panel">
      <div className="flex gap-2" role="group" aria-label="Distribution axis">
        {(["pnl", "r_multiple"] as const).map((m) => (
          <button
            key={m}
            type="button"
            data-testid={`dist-mode-${m === "pnl" ? "pnl" : "r"}`}
            aria-pressed={mode === m}
            onClick={() => setMode(m)}
            className={cn(
              "rounded-md border px-3 py-1.5 text-sm font-medium transition-colors",
              mode === m
                ? "border-primary bg-primary/10 text-on-surface"
                : "border-outline-variant text-on-surface-variant hover:text-on-surface",
            )}
          >
            {m === "pnl" ? "P&L" : "R-Multiple"}
          </button>
        ))}
      </div>
      <p className="text-sm text-on-surface-variant">
        {formatInteger(activeCount)}{" "}
        {mode === "pnl" ? "closed trades" : "trades with planned risk"}, grouped by per-trade{" "}
        {mode === "pnl" ? "net P&L" : "R-multiple"}.
      </p>
      <div className="space-y-2">
        {buckets.map((bucket) => (
          <div
            key={bucket.bucket_id}
            className="grid grid-cols-[10rem_1fr_3rem] items-center gap-3"
            data-testid={`dist-bucket-${bucket.bucket_id}`}
          >
            <div className="flex flex-col">
              <span className="text-sm font-medium text-on-surface">
                {DIST_BAND_LABELS[bucket.bucket_id] ?? bucket.bucket_id}
              </span>
              <span className="text-xs text-on-surface-variant">
                {mode === "pnl" ? pnlBandRange(bucket.bucket_id, edge) : rBandRange(bucket.bucket_id)}
              </span>
            </div>
            <div className="h-3 rounded-full bg-surface-container-high">
              <div
                className="h-3 rounded-full bg-primary"
                style={{ width: maxCount === 0 ? "0%" : `${(bucket.count / maxCount) * 100}%` }}
              />
            </div>
            <span className="text-right text-sm tabular-nums text-on-surface">
              {formatInteger(bucket.count)}
            </span>
          </div>
        ))}
      </div>
      {mode === "r_multiple" && data.r_excluded_count > 0 && (
        <p className="text-xs text-on-surface-variant" data-testid="dist-r-excluded">
          {formatInteger(data.r_excluded_count)} trade
          {data.r_excluded_count === 1 ? "" : "s"} excluded (no planned risk defined).
        </p>
      )}
    </div>
  );
}

export function OverallStatisticsPage() {
  const globalFilters = useGlobalFilters();
  const [activeView, setActiveView] = useState<ViewId>("pnl");

  // Every analytics tab honours the shared FilterBar directly now; the old
  // per-page "apply global date filter" toggle was dropped (clear the FilterBar
  // date for an all-time view). Three query sources back the nine views: the
  // seven aggregate tabs (overall-statistics), Daily P&L (pnl-by-day), and the
  // per-trade Distribution (pnl-distribution).
  const statsQuery = useOverallStatistics(globalFilters);
  const pnlQuery = usePnLByDay(globalFilters);
  const distQuery = useDistribution(globalFilters);

  const activeQuery =
    activeView === "daily_pnl"
      ? pnlQuery
      : activeView === "distribution"
        ? distQuery
        : statsQuery;

  return (
    <main className="space-y-6 p-6">
      <header className="flex items-baseline justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-medium tracking-tight text-on-surface">Statistics</h1>
          <p className="text-sm text-on-surface-variant">
            Aggregated performance across every view; the active FilterBar scopes all tabs.
          </p>
        </div>
      </header>

      <nav
        className="flex flex-wrap gap-2 border-b border-outline-variant"
        role="tablist"
        aria-label="Statistics views"
      >
        {VIEW_IDS.map((id) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={activeView === id}
            data-testid={`view-tab-${id}`}
            onClick={() => setActiveView(id)}
            className={cn(
              "rounded-t-md border-b-2 px-3 py-2 text-sm font-medium transition-colors",
              activeView === id
                ? "border-primary text-on-surface"
                : "border-transparent text-on-surface-variant hover:text-on-surface",
            )}
          >
            {VIEW_LABELS[id]}
          </button>
        ))}
      </nav>

      {activeQuery.isLoading && (
        <div className="text-sm text-on-surface-variant" data-testid="overall-stats-loading">
          Loading statistics…
        </div>
      )}
      {activeQuery.error !== null && activeQuery.error !== undefined && (
        <div className="text-sm text-error" data-testid="overall-stats-error">
          Failed to load statistics. Try changing the filter and reloading.
        </div>
      )}
      {isStatsView(activeView) && statsQuery.data !== undefined && (
        <section
          role="tabpanel"
          aria-labelledby={`view-tab-${activeView}`}
          data-testid={`view-panel-${activeView}`}
        >
          {activeView === "pnl" && (
            <PnLViewPanel view={statsQuery.data.pnl} currency={statsQuery.data.currency} />
          )}
          {activeView === "scores_metrics" && (
            <ScoresMetricsViewPanel view={statsQuery.data.scores_metrics} />
          )}
          {activeView === "total_trades" && (
            <TotalTradesViewPanel view={statsQuery.data.total_trades} />
          )}
          {activeView === "volume" && <VolumeViewPanel view={statsQuery.data.volume} />}
          {activeView === "symbol" && <SymbolViewPanel view={statsQuery.data.symbol} />}
          {activeView === "trade_duration" && (
            <TradeDurationViewPanel view={statsQuery.data.trade_duration} />
          )}
          {activeView === "commissions" && (
            <CommissionsViewPanel view={statsQuery.data.commissions} />
          )}
        </section>
      )}
      {activeView === "daily_pnl" && pnlQuery.data !== undefined && (
        <section
          role="tabpanel"
          aria-labelledby="view-tab-daily_pnl"
          data-testid="view-panel-daily_pnl"
        >
          <DailyPnLViewPanel
            points={pnlQuery.data.points}
            currency={pnlQuery.data.currency}
          />
        </section>
      )}
      {activeView === "distribution" && distQuery.data !== undefined && (
        <section
          role="tabpanel"
          aria-labelledby="view-tab-distribution"
          data-testid="view-panel-distribution"
        >
          <DistributionViewPanel data={distQuery.data} />
        </section>
      )}
    </main>
  );
}
